import { useRef } from 'react'
import { useBreakpoints, useView } from '@10000/react'
import { Box, CenteredBox, ElementHighlight, PracticePie, useTooltipSeries } from '../widgets'
import { PlaybackControls, PracticeDetailsCard, PracticeResults, PracticeResultsCard } from './'
import { practiceAverages, practiceConsensusStats } from '../../utilities.js'
import { average } from '@10000/common/src/functional.js'
import { useAnalyticsData } from './AnalyticsData.jsx'
import { GuidanceButton } from './GuidanceButton.jsx'
import { isFree } from '../../lookups'
import './PracticeAnalytics.scss'

export const PracticeAnalytics = ({ displayMode }) => {
  const { active, practice, setPractice, detailOpen, setDetailOpen, setBreakdownOpen, setGuidanceOpen, transition } = useAnalyticsData()
  const { subscription } = useView('tenant')
  const free = isFree(subscription)

  const { next, tip } = useTooltipSeries()
  const { constrained } = useBreakpoints()
  const pie = useRef()
  const mobileImproveButton = useRef()

  const values = active?.teamAverage
    ? practiceAverages(active.teamAverage.map(average))
    : []
  const changeValues = active?.practiceChange || []
  const { touches, timeTaken } = practiceConsensusStats(active?.consensus, practice)

  const openDetail = practice => {
    if (typeof values[practice] === 'number' && !isNaN(values[practice])) {
      setPractice(practice)
      setDetailOpen(true)
      if (tip === 2) {
        next()
      }
    }
  }

  const closeDetail = () => {
    setDetailOpen(false)
    setTimeout(() => setPractice(undefined), 500)
  }

  const openGuidance = () => setGuidanceOpen(true)

  return (
    <>
      <Box
        fullWidth
        alignItems="center"
        justifyContent="space-around"
        height="100%"
        maxHeight="100vw"
      >
        <CenteredBox
          opacity={detailOpen ? 1 : 0}
          transition="0.5s"
          className="wide-layout"
          width={330}
          zIndex={1}
        >
          <PracticeDetailsCard
            practice={practice}
            onClick={() => setBreakdownOpen(true)}
            onClose={closeDetail}
            displayMode={displayMode}
          />
        </CenteredBox>

        <Box
          height="100%"
          aspect="1/1"
          className="pie-container"
          maxHeight={displayMode ? '100%' : undefined}
        >
          <PracticePie
            ref={pie}
            values={values}
            selectedPractice={practice}
            onPracticeSelected={displayMode ? undefined : openDetail}
            transition={transition}
            showAllHeadings
          />
        </Box>

        <CenteredBox
          width={330}
          opacity={detailOpen ? 1 : 0}
          transition="0.5s"
          className="wide-layout"
        >
          <PracticeResults
            value={values[practice]}
            change={changeValues[practice]}
            touches={touches}
            timeTaken={timeTaken}
            onGuidanceClick={openGuidance}
            displayMode={displayMode}
          />
        </CenteredBox>
      </Box>

      <CenteredBox
        column
        position="absolute"
        inset
        className="stacked-layout"
        opacity={detailOpen ? 1 : 0}
        zIndex={detailOpen ? 10 : -1}
        gap={8}
        transition="0.5s"
        onClick={closeDetail}
      >
        <PracticeResultsCard
          practice={practice}
          value={values[practice]}
          change={undefined}
          rank={35}
          onClose={closeDetail}
          onClick={() => setBreakdownOpen(true)}
          displayMode={displayMode}
        />
        {!displayMode &&
          <Box onClick={e => e.stopPropagation()}>
            <GuidanceButton onClick={openGuidance} ref={mobileImproveButton} />
          </Box>
        }
      </CenteredBox>

      {!displayMode &&
        <>
          <ElementHighlight
            order={2}
            target={pie}
            circle
            interactive
            pointDirection={constrained ? 'down' : 'right-down'}
          />

          {constrained && (
            <ElementHighlight
              order={6}
              target={mobileImproveButton}
              pointDirection="down"
              nextOnFinish
              onFinish={closeDetail}
            />
          )}
        </>
      }

      {(!displayMode && !free) &&
        <PlaybackControls />
      }
    </>
  )
}